import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, Plus, ArrowRight, Route as RouteIcon, Receipt, FileText, Settings } from "lucide-react";
import { StatCard, TypeBadge } from "@/components/Shared";
import { TripDialog } from "@/components/TripDialog";
import { ExpenseDialog } from "@/components/ExpenseDialog";
import { api, fmtDate, fmtMiles, fmtMoney } from "@/lib/api";
import { useSubscription } from "@/context/SubscriptionContext";

const HeroDeduction = ({ d, onTrip, onExpense }) => (
  <section className="relative overflow-hidden rounded-3xl bg-emerald-950 text-white p-6 sm:p-10 mt-fade-up" data-testid="dashboard-hero">
    <div className="absolute -right-24 -top-24 w-80 h-80 rounded-full bg-emerald-700/30 blur-3xl" />
    <div className="relative grid lg:grid-cols-[1fr_auto] gap-8 items-end">
      <div>
        <p className="mt-overline !text-emerald-300">Estimated mileage deduction · {d.year}</p>
        <p className="font-mono text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mt-3" data-testid="stat-mileage-deduction">
          {fmtMoney(d.year_mileage_deduction)}
        </p>
        <p className="text-emerald-100/80 mt-3 text-sm sm:text-base">
          {fmtMiles(d.year_business_miles)} business miles × IRS rate for each trip's date · Estimate only
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
        <button onClick={onTrip} className="mt-btn-primary !bg-emerald-500 hover:!bg-emerald-400 !text-emerald-950 !h-16 !text-lg sm:!px-10" data-testid="track-trip-button">
          <Car className="w-6 h-6" />
          Track a Trip
        </button>
        <button onClick={onExpense} className="mt-btn-dark !bg-white/10 hover:!bg-white/20 !h-16 border border-white/20" data-testid="add-expense-button">
          <Plus className="w-5 h-5" />
          Add Expense
        </button>
      </div>
    </div>
  </section>
);

const QUICK = [
  { to: "/mileage", label: "Mileage Log", icon: RouteIcon, id: "quick-link-mileage" },
  { to: "/expenses", label: "Expenses", icon: Receipt, id: "quick-link-expenses" },
  { to: "/tax-report", label: "Tax Report", icon: FileText, id: "quick-link-tax-report" },
  { to: "/settings", label: "Settings", icon: Settings, id: "quick-link-settings" },
];

const QuickLinks = () => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
    {QUICK.map(({ to, label, icon: Icon, id }) => (
      <Link key={to} to={to} data-testid={id} className="mt-card p-4 sm:p-5 flex items-center justify-between group hover:border-emerald-300 transition-colors">
        <span className="flex items-center gap-3 font-semibold text-slate-800">
          <span className="w-10 h-10 rounded-xl bg-slate-100 group-hover:bg-emerald-50 flex items-center justify-center transition-colors">
            <Icon className="w-5 h-5 text-slate-700 group-hover:text-emerald-700" />
          </span>
          {label}
        </span>
        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
      </Link>
    ))}
  </div>
);

const Recent = ({ title, to, items, render, empty, testId }) => (
  <div className="mt-card p-5 sm:p-6" data-testid={testId}>
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-heading text-lg font-bold text-slate-900">{title}</h3>
      <Link to={to} className="text-sm font-semibold text-emerald-700 hover:text-emerald-900">View all</Link>
    </div>
    {items.length ? <ul className="divide-y divide-slate-100">{items.map(render)}</ul> : <p className="text-slate-500 text-sm py-6 text-center">{empty}</p>}
  </div>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const { requirePaid } = useSubscription();
  const [tripOpen, setTripOpen] = useState(false);
  const [expOpen, setExpOpen] = useState(false);
  const load = useCallback(() => api.get("/dashboard").then(({ data }) => setD(data)), []);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="h-64 rounded-3xl bg-slate-200/60 animate-pulse" data-testid="dashboard-loading" />;

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="dashboard-page">
      <div className="mt-fade-up">
        <p className="mt-overline mb-1">Dashboard</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="dashboard-greeting">
          Hi{d.name ? `, ${d.name.split(" ")[0]}` : ""}
        </h1>
        {d.business_name && <p className="text-slate-500 mt-1">{d.business_name}</p>}
      </div>
      <HeroDeduction d={d} onTrip={requirePaid(() => setTripOpen(true))} onExpense={requirePaid(() => setExpOpen(true))} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <StatCard label={`Business miles · ${d.month_label}`} value={fmtMiles(d.month_business_miles)} sub="miles this month" testId="stat-month-miles" accent="green" delay={60} />
        <StatCard label={`Business miles · ${d.year}`} value={fmtMiles(d.year_business_miles)} sub="miles this year" testId="stat-year-miles" accent="green" delay={120} />
        <StatCard label={`Expenses · ${d.month_label}`} value={fmtMoney(d.month_expenses)} sub="spent this month" testId="stat-month-expenses" delay={180} />
        <StatCard label={`Expenses · ${d.year}`} value={fmtMoney(d.year_expenses)} sub="spent this year" testId="stat-year-expenses" delay={240} />
      </div>
      <QuickLinks />
      <div className="grid lg:grid-cols-2 gap-5">
        <Recent
          title="Recent trips" to="/mileage" items={d.recent_trips} empty="No trips yet. Tap Track a Trip to add one." testId="recent-trips"
          render={(t) => (
            <li key={t.id} className="py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 truncate">{t.purpose || t.client || "Trip"}</p>
                <p className="text-sm text-slate-500">{fmtDate(t.date)}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <TypeBadge type={t.trip_type} />
                <span className="font-mono font-bold text-slate-900 w-20 text-right">{fmtMiles(t.miles)} mi</span>
              </div>
            </li>
          )}
        />
        <Recent
          title="Recent expenses" to="/expenses" items={d.recent_expenses} empty="No expenses yet. Tap Add Expense to add one." testId="recent-expenses"
          render={(e) => (
            <li key={e.id} className="py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 truncate">{e.description || e.category}</p>
                <p className="text-sm text-slate-500">{fmtDate(e.date)} · {e.category}</p>
              </div>
              <span className="font-mono font-bold text-slate-900 shrink-0">{fmtMoney(e.amount)}</span>
            </li>
          )}
        />
      </div>
      <TripDialog open={tripOpen} onOpenChange={setTripOpen} onSaved={load} />
      <ExpenseDialog open={expOpen} onOpenChange={setExpOpen} onSaved={load} />
    </div>
  );
}
